<template>
  <Dialog
    :visible="visible"
    modal
    class="migration-v5-modal"
    :show-header="false"
    :style="{ width: '42rem' }"
    :breakpoints="{ '1199px': '70vw', '575px': '95vw' }"
    :draggable="false"
    :close-on-escape="!loading"
    :dismissable-mask="false"
    @update:visible="onDismiss"
  >
    <div class="migration-modal">
      <button
        type="button"
        class="migration-modal__close"
        :disabled="loading"
        :aria-label="t('migrationV5.modal.later')"
        @click="handleLater"
      >
        <i class="fa-light fa-xmark" />
      </button>

      <!-- ========== HERO ========== -->
      <div class="migration-modal__hero">
        <div class="migration-modal__icon">
          <span class="migration-modal__icon-badge">
            <i class="fa-light fa-rocket-launch" />
          </span>
        </div>
        <p class="migration-modal__eyebrow">
          {{ t('migrationV5.modal.stepCounter', { current: currentStepIndex + 1, total: steps.length }) }}
        </p>
        <h2 class="migration-modal__title">
          {{ t('migrationV5.modal.title') }}
        </h2>
        <p class="migration-modal__subtitle">
          {{ t('migrationV5.modal.subtitle') }}
        </p>
      </div>

      <!-- ========== STEPPER ========== -->
      <ol class="migration-modal__stepper">
        <li
          v-for="(s, i) in steps"
          :key="s.key"
          class="migration-modal__step"
          :class="{
            'is-active': i === currentStepIndex,
            'is-done': i < currentStepIndex,
          }"
        >
          <span class="migration-modal__step-node">
            <i
              v-if="i < currentStepIndex"
              class="fa-light fa-check"
            />
            <template v-else>{{ i + 1 }}</template>
          </span>
          <span class="migration-modal__step-label">{{ t(s.label) }}</span>
        </li>
      </ol>

      <!-- ========== BODY ========== -->
      <div class="migration-modal__body">
        <!-- STEP 1 — OVERVIEW -->
        <template v-if="currentStep === 'overview'">
          <p class="migration-modal__lead">
            {{ t('migrationV5.modal.overview.intro') }}
          </p>
          <ul class="migration-modal__features">
            <li class="migration-modal__feature">
              <span class="migration-modal__feature-icon">
                <i class="fa-light fa-arrow-right-arrow-left" />
              </span>
              <div class="migration-modal__feature-text">
                <p class="migration-modal__feature-title">
                  {{ t('migrationV5.modal.overview.point1Title') }}
                </p>
                <p class="migration-modal__feature-body">
                  {{ t('migrationV5.modal.overview.point1Body') }}
                </p>
              </div>
            </li>
            <li class="migration-modal__feature">
              <span class="migration-modal__feature-icon">
                <i class="fa-light fa-puzzle-piece" />
              </span>
              <div class="migration-modal__feature-text">
                <p class="migration-modal__feature-title">
                  {{ t('migrationV5.modal.overview.point2Title') }}
                </p>
                <p class="migration-modal__feature-body">
                  {{ t('migrationV5.modal.overview.point2Body') }}
                </p>
              </div>
            </li>
            <li class="migration-modal__feature">
              <span class="migration-modal__feature-icon">
                <i class="fa-light fa-wand-magic-sparkles" />
              </span>
              <div class="migration-modal__feature-text">
                <p class="migration-modal__feature-title">
                  {{ t('migrationV5.modal.overview.point3Title') }}
                </p>
                <p class="migration-modal__feature-body">
                  {{ t('migrationV5.modal.overview.point3Body') }}
                </p>
              </div>
            </li>
          </ul>
          <div class="migration-modal__duration">
            <i class="fa-light fa-clock" />
            <span>{{ t('migrationV5.modal.overview.duration') }}</span>
          </div>
        </template>

        <!-- STEP 2 — REQUIREMENTS -->
        <template v-else-if="currentStep === 'requirements'">
          <p class="migration-modal__lead">
            {{ t('migrationV5.modal.requirements.intro') }}
          </p>
          <div class="migration-modal__callout migration-modal__callout--tech">
            <span class="migration-modal__callout-icon">
              <i class="fa-light fa-user-gear" />
            </span>
            <div>
              <p class="migration-modal__callout-title">
                {{ t('migrationV5.modal.requirements.techTitle') }}
              </p>
              <p class="migration-modal__callout-body">
                {{ t('migrationV5.modal.requirements.techBody') }}
              </p>
            </div>
          </div>
          <ul class="migration-modal__checklist">
            <li>
              <i class="fa-light fa-circle-check" />
              <span>{{ t('migrationV5.modal.requirements.item1') }}</span>
            </li>
            <li>
              <i class="fa-light fa-circle-check" />
              <span>{{ t('migrationV5.modal.requirements.item2') }}</span>
            </li>
            <li>
              <i class="fa-light fa-circle-check" />
              <span>{{ t('migrationV5.modal.requirements.item3') }}</span>
            </li>
          </ul>
        </template>

        <!-- STEP 3 — CONFIRM (benefits / what you gain) -->
        <template v-else>
          <p class="migration-modal__lead">
            {{ t('migrationV5.modal.confirm.intro') }}
          </p>
          <ul class="migration-modal__features">
            <li class="migration-modal__feature">
              <span class="migration-modal__feature-icon">
                <i class="fa-light fa-gauge-high" />
              </span>
              <div class="migration-modal__feature-text">
                <p class="migration-modal__feature-title">
                  {{ t('migrationV5.modal.confirm.benefit1Title') }}
                </p>
                <p class="migration-modal__feature-body">
                  {{ t('migrationV5.modal.confirm.benefit1Body') }}
                </p>
              </div>
            </li>
            <li class="migration-modal__feature">
              <span class="migration-modal__feature-icon">
                <i class="fa-light fa-bolt" />
              </span>
              <div class="migration-modal__feature-text">
                <p class="migration-modal__feature-title">
                  {{ t('migrationV5.modal.confirm.benefit2Title') }}
                </p>
                <p class="migration-modal__feature-body">
                  {{ t('migrationV5.modal.confirm.benefit2Body') }}
                </p>
              </div>
            </li>
            <li class="migration-modal__feature">
              <span class="migration-modal__feature-icon">
                <i class="fa-light fa-shield-check" />
              </span>
              <div class="migration-modal__feature-text">
                <p class="migration-modal__feature-title">
                  {{ t('migrationV5.modal.confirm.benefit3Title') }}
                </p>
                <p class="migration-modal__feature-body">
                  {{ t('migrationV5.modal.confirm.benefit3Body') }}
                </p>
              </div>
            </li>
          </ul>
          <p class="migration-modal__hint">
            <i class="fa-light fa-circle-info" />
            <span>{{ t('migrationV5.modal.confirm.finalNote') }}</span>
          </p>
        </template>
      </div>

      <!-- ========== FOOTER ========== -->
      <div class="migration-modal__footer">
        <SpmButton
          v-if="currentStepIndex === 0"
          :label="t('migrationV5.modal.later')"
          :disabled="loading"
          class-style="p-button p-button-secondary"
          @click="handleLater"
        />
        <SpmButton
          v-else
          :label="t('migrationV5.modal.back')"
          :disabled="loading"
          class-style="p-button p-button-secondary"
          @click="goBack"
        />

        <SpmButton
          v-if="currentStep !== 'confirm'"
          :label="t('migrationV5.modal.next')"
          :disabled="loading"
          class-style="p-button p-button-primary"
          @click="goNext"
        />
        <SpmButton
          v-else
          :label="t('migrationV5.modal.confirmStart')"
          icon="fa-light fa-rocket-launch"
          :loading="loading"
          class-style="p-button p-button-primary"
          @click="handleConfirm"
        />
      </div>
    </div>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import {
  MigrationV5State,
  closeMigrationV5Modal,
  snoozeModal,
  startMigrationV5,
  redirectToV5,
} from '@/composables/migrationV5';
import { showToastError } from '@/helpers';

type StepKey = 'overview' | 'requirements' | 'confirm';

const STEPS: { key: StepKey; label: string }[] = [
  { key: 'overview', label: 'migrationV5.modal.stepper.overview' },
  { key: 'requirements', label: 'migrationV5.modal.stepper.requirements' },
  { key: 'confirm', label: 'migrationV5.modal.stepper.confirm' },
];

export default defineComponent({
  name: 'MigrationV5Modal',
  components: {
    Dialog,
    SpmButton,
  },

  setup() {
    const { t } = useI18n();
    const currentStep = ref<StepKey>('overview');
    const loading = ref(false);

    const visible = computed(() => MigrationV5State.modalOpen);
    const currentStepIndex = computed(() => STEPS.findIndex((s) => s.key === currentStep.value));

    const reset = () => {
      currentStep.value = 'overview';
      loading.value = false;
    };

    const goNext = () => {
      const i = currentStepIndex.value;
      if (i < STEPS.length - 1) currentStep.value = STEPS[i + 1].key;
    };

    const goBack = () => {
      const i = currentStepIndex.value;
      if (i > 0) currentStep.value = STEPS[i - 1].key;
    };

    const handleLater = () => {
      snoozeModal();
      closeMigrationV5Modal();
      reset();
    };

    const onDismiss = (next: boolean) => {
      if (next === false && !loading.value) {
        handleLater();
      }
    };

    const handleConfirm = async () => {
      loading.value = true;
      try {
        await startMigrationV5();
        redirectToV5();
      } catch (e) {
        loading.value = false;
        await showToastError('GENERIC_ERROR');
      }
    };

    return {
      t,
      steps: STEPS,
      currentStep,
      currentStepIndex,
      loading,
      visible,
      goNext,
      goBack,
      handleLater,
      handleConfirm,
      onDismiss,
    };
  },
});
</script>

<style lang="scss" scoped>
$mig-green: #94c840;
$mig-green-mid: #62a02a;
$mig-green-deep: #4a861f;
$mig-ink: #2b3a4b;
$mig-muted: #64758a;

.migration-modal {
  position: relative;
  display: flex;
  flex-direction: column;

  &__close {
    position: absolute;
    top: 0.9rem;
    right: 0.9rem;
    z-index: 2;
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.18);
    color: #fff;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.32);
    }
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    i {
      font-size: 1rem;
    }
  }

  /* ---------- HERO (dark login palette: charcoal-green + brand-green glows) ---------- */
  &__hero {
    position: relative;
    padding: 2.25rem 1.75rem 1.75rem;
    text-align: center;
    color: #fff;
    background:
      radial-gradient(circle at 85% 12%, rgba(148, 200, 64, 0.28) 0%, transparent 45%),
      radial-gradient(circle at 8% 92%, rgba(148, 200, 64, 0.16) 0%, transparent 42%),
      linear-gradient(150deg, #283024 0%, #1c211b 55%, #161a15 100%);
    overflow: hidden;
  }

  &__icon {
    position: relative;
    display: flex;
    justify-content: center;
    margin-bottom: 1rem;
  }

  &__icon-badge {
    position: relative;
    z-index: 1;
    width: 68px;
    height: 68px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(148, 200, 64, 0.18);
    border: 1px solid rgba(148, 200, 64, 0.5);
    box-shadow: 0 10px 26px rgba(0, 0, 0, 0.25);

    i {
      font-size: 1.85rem;
      color: #fff;
    }
  }

  &__eyebrow {
    margin: 0 0 0.45rem;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.85);
  }

  &__title {
    margin: 0 0 0.5rem;
    font-size: 1.4rem;
    font-weight: 700;
    line-height: 1.25;
    letter-spacing: -0.01em;
    color: #fff;
  }

  &__subtitle {
    margin: 0 auto;
    max-width: 30rem;
    font-size: 0.92rem;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.9);
  }

  /* ---------- STEPPER ---------- */
  &__stepper {
    display: flex;
    margin: 0;
    padding: 1.4rem 1.75rem 0.4rem;
    list-style: none;
  }

  &__step {
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;

    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 14px;
      height: 2px;
      background: #e3e8ee;
      z-index: 0;
    }
    &::before {
      left: 0;
      right: 50%;
    }
    &::after {
      left: 50%;
      right: 0;
    }
    &:first-child::before {
      display: none;
    }
    &:last-child::after {
      display: none;
    }

    &.is-done::before,
    &.is-done::after,
    &.is-active::before {
      background: $mig-green-deep;
    }
  }

  &__step-node {
    position: relative;
    z-index: 1;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.85rem;
    font-weight: 700;
    background: #f0f3f7;
    color: #9aa7b4;
    border: 2px solid #e3e8ee;
    transition: all 0.2s ease;

    i {
      font-size: 0.8rem;
    }
  }

  &__step-label {
    margin-top: 0.45rem;
    font-size: 0.72rem;
    font-weight: 600;
    color: #9aa7b4;
    text-align: center;
  }

  &__step.is-active &__step-node {
    background: linear-gradient(135deg, $mig-green 0%, $mig-green-mid 100%);
    border-color: transparent;
    color: #fff;
    box-shadow: 0 4px 10px rgba(74, 134, 31, 0.35);
  }
  &__step.is-active &__step-label {
    color: $mig-green-deep;
  }

  &__step.is-done &__step-node {
    background: $mig-green-mid;
    border-color: $mig-green-mid;
    color: #fff;
  }
  &__step.is-done &__step-label {
    color: $mig-green-deep;
  }

  /* ---------- BODY ---------- */
  &__body {
    padding: 1.4rem 1.75rem 0.5rem;
    text-align: left;
  }

  &__lead {
    margin: 0 0 1.15rem;
    font-size: 0.95rem;
    line-height: 1.55;
    color: $mig-ink;
  }

  &__features {
    list-style: none;
    margin: 0 0 1.15rem;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
  }

  &__feature {
    display: flex;
    align-items: flex-start;
    gap: 0.85rem;
  }

  &__feature-icon {
    flex-shrink: 0;
    width: 42px;
    height: 42px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(148, 200, 64, 0.14);
    color: $mig-green-deep;

    i {
      font-size: 1.1rem;
    }
  }

  &__feature-title {
    margin: 0 0 0.15rem;
    font-size: 0.92rem;
    font-weight: 700;
    color: $mig-ink;
  }

  &__feature-body {
    margin: 0;
    font-size: 0.86rem;
    line-height: 1.45;
    color: $mig-muted;
  }

  &__duration {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.55rem 0.9rem;
    border-radius: 10px;
    background: rgba(148, 200, 64, 0.12);
    color: $mig-green-deep;
    font-size: 0.85rem;
    font-weight: 600;

    i {
      font-size: 0.95rem;
    }
  }

  /* ---------- CALLOUTS ---------- */
  &__callout {
    display: flex;
    align-items: flex-start;
    gap: 0.85rem;
    padding: 1rem 1.1rem;
    border-radius: 12px;
    margin-bottom: 1.15rem;

    &-icon {
      flex-shrink: 0;
      width: 38px;
      height: 38px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;

      i {
        font-size: 1.05rem;
      }
    }

    &-title {
      margin: 0 0 0.25rem;
      font-size: 0.92rem;
      font-weight: 700;
    }

    &-body {
      margin: 0;
      font-size: 0.86rem;
      line-height: 1.5;
      color: $mig-muted;
    }

    &--tech {
      background: #fdf6e7;
      border: 1px solid #f6e2b0;

      .migration-modal__callout-icon {
        background: rgba(240, 183, 47, 0.18);
        color: #b67e09;
      }
      .migration-modal__callout-title {
        color: #8a5e00;
      }
    }
  }

  /* ---------- CHECKLIST ---------- */
  &__checklist {
    list-style: none;
    margin: 0 0 0.5rem;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.65rem;

    li {
      display: flex;
      align-items: flex-start;
      gap: 0.6rem;
      font-size: 0.88rem;
      line-height: 1.4;
      color: $mig-ink;
    }
    i {
      margin-top: 0.1rem;
      color: $mig-green-deep;
      font-size: 0.95rem;
    }
  }

  /* ---------- HINT ---------- */
  &__hint {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    font-size: 0.84rem;
    color: $mig-muted;

    i {
      color: $mig-green-deep;
    }
  }

  /* ---------- FOOTER ---------- */
  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 1.25rem 1.75rem 1.5rem;
    margin-top: 0.75rem;
    border-top: 1px solid #eef1f5;
  }
}
</style>

<style lang="scss">
/* Dialog chrome: header is hidden (we render a custom hero), so round the
   content's top corners and strip the default PrimeVue content padding —
   the hero is full-bleed and every inner section pads itself. */
.migration-v5-modal.p-dialog {
  border-radius: 16px;
  overflow: hidden;

  .p-dialog-content {
    padding: 0;
    border-radius: 16px;
  }
}
</style>
